import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../../../shared/components/PageHeader';
import { EmptyState } from '../../../shared/components/EmptyState';
import { ErrorAlert } from '../../../shared/components/ErrorAlert';
import { Spinner } from '../../../shared/components/Spinner';
import { useDocumentTitle } from '../../../shared/hooks/useDocumentTitle';
import { useAuth } from '../../../contexts/AuthContext';
import type { ShopifyComplianceRequest, ShopifyComplianceRequestFilters } from '../../../models/shopifyComplianceRequest';
import { isShopifyComplianceRequestOverdue } from '../../../models/shopifyComplianceRequest';
import { fetchShopifyComplianceRequests } from '../../../services/admin/shopifyComplianceService';

const TOPIC_OPTIONS = [
  { value: '', label: 'All topics' },
  { value: 'customers/data_request', label: 'customers/data_request' },
  { value: 'customers/redact', label: 'customers/redact' },
  { value: 'shop/redact', label: 'shop/redact' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'Received', label: 'Received' },
  { value: 'Completed', label: 'Completed' },
  { value: 'Retained', label: 'Retained' },
  { value: 'Failed', label: 'Failed' },
];

function statusPillClass(status: string): string {
  const normalized = status.toLowerCase();
  if (normalized === 'completed') {
    return 'bo-status-active';
  }
  if (normalized === 'failed') {
    return 'bo-status-expired';
  }
  return 'bo-status-inactive';
}

function formatDateTime(value: string | null): string {
  if (!value) {
    return '—';
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}

export function ShopifyComplianceRequestListPage(): React.ReactElement {
  useDocumentTitle('Shopify compliance requests · Triplicity Backoffice');
  const { getAccessToken } = useAuth();

  const [requests, setRequests] = useState<ShopifyComplianceRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<ShopifyComplianceRequestFilters>({});

  const reload = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const rows = await fetchShopifyComplianceRequests(getAccessToken, filters);
      setRequests(rows);
    } catch (err) {
      setRequests([]);
      setError(err instanceof Error ? err.message : 'Failed to load Shopify compliance requests.');
    } finally {
      setLoading(false);
    }
  }, [getAccessToken, filters]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const handleFilterChange = (next: ShopifyComplianceRequestFilters): void => {
    setFilters(next);
  };

  return (
    <div>
      <PageHeader
        title="Shopify compliance requests"
        subtitle="Mandatory GDPR/App-Store webhooks: customer data requests, customer redaction, and shop redaction. Complete within 30 days of receipt."
      />

      <div className="bo-admin-toolbar bo-card">
        <label className="bo-field" htmlFor="shopify-compliance-topic">
          <span className="bo-field-label">Topic</span>
          <select
            id="shopify-compliance-topic"
            className="bo-input"
            value={filters.topic ?? ''}
            onChange={(event) => handleFilterChange({ ...filters, topic: event.target.value })}
          >
            {TOPIC_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="bo-field" htmlFor="shopify-compliance-status">
          <span className="bo-field-label">Status</span>
          <select
            id="shopify-compliance-status"
            className="bo-input"
            value={filters.status ?? ''}
            onChange={(event) => handleFilterChange({ ...filters, status: event.target.value })}
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {loading ? (
        <div className="bo-card bo-detail-loading">
          <Spinner label="Loading Shopify compliance requests…" />
        </div>
      ) : null}

      {!loading && error ? <ErrorAlert message={error} /> : null}

      {!loading && !error && requests.length === 0 ? (
        <EmptyState
          title="No compliance requests"
          description="No Shopify mandatory compliance webhook requests match the current filters."
        />
      ) : null}

      {!loading && !error && requests.length > 0 ? (
        <div className="bo-card">
          <div className="bo-admin-table-wrap">
            <table className="bo-admin-table">
              <thead>
                <tr>
                  <th>Topic</th>
                  <th>Shop domain</th>
                  <th>Merchant</th>
                  <th>Status</th>
                  <th>Received</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {requests.map((request) => {
                  const overdue = isShopifyComplianceRequestOverdue(request);
                  return (
                    <tr key={request.id}>
                      <td>{request.topic}</td>
                      <td>{request.shopDomain}</td>
                      <td>{request.merchantId ?? <span className="bo-muted">Unknown shop</span>}</td>
                      <td>
                        <span className={`bo-status-pill ${statusPillClass(request.status)}`}>{request.status}</span>
                      </td>
                      <td>
                        {formatDateTime(request.receivedAt)}
                        {overdue ? (
                          <span className="bo-status-pill bo-status-expired" style={{ marginLeft: '0.5rem' }}>
                            Over 20 days
                          </span>
                        ) : null}
                      </td>
                      <td>
                        <Link className="bo-btn bo-btn-secondary" to={`/admin/shopify-compliance-requests/${request.id}`}>
                          View
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}
